/**
 * Client onboarding consent — per-purpose taxonomy (SSOT).
 *
 * Implements docs/specs/SAH_ONBOARDING_CONSENT_DESIGN.md:
 *  - §1  ONE hard gate (signed service agreement) + ONE required consent
 *        (collect personal/health info, APP 3.3). Everything else is an
 *        authorised acknowledgement, an optional opt-in, or a record.
 *  - §4  Three visually-distinct tiers with provider read-aloud copy.
 *  - §5  One row per purpose written to `public.consents` via the controlled
 *        writer `record_client_consent_controlled`.
 *
 * Purpose tokens mirror the `consents.consent_type` CHECK. `PHOTOS` (the legacy
 * token) is reused for the photography opt-in — there is no separate PHOTOGRAPHY
 * concept. `USE_SHARE_CARE` ABSORBS the deleted standalone associate/sub-provider
 * data-sharing consent (design §2) — the disclosure is care-purpose and already
 * authorised (Aged Care Act 2024 ss 537–538); transparency is discharged by the
 * service agreement naming the associated provider + this acknowledgement line.
 */

/**
 * Version stamp for the presented consent-pack text. Stored on each consent row
 * (document_version) so we know exactly which read-aloud copy the participant was
 * given. Bump when the read-aloud copy below changes materially.
 */
export const CLIENT_CONSENT_PACK_VERSION = 'sah-consent-v2-2026-07';

/** Consent tier — drives the visually-distinct grouping. Tier C never bundles into A/B. */
export type ClientConsentTier = 'REQUIRED' | 'ACKNOWLEDGE' | 'OPTIONAL';

/** WHO affirmed the consent (design §3/§5). A supporter never consents FOR the
 * person, so a supporter-assisted consent is still `CLIENT`. */
export type ConsentedBy = 'CLIENT' | 'DECISION_MAKER';

/** Per-purpose tokens stored in `consents.consent_type` for the redesign. */
export type ClientConsentPurpose =
  | 'SERVICE_AGREEMENT'
  | 'COLLECT_INFO'
  | 'USE_SHARE_CARE'
  | 'CONTACT_NAMED_PERSON'
  | 'PHOTOS'
  | 'MHR_PREFERENCE'
  | 'ACD_RECORD';

export interface ClientConsentDef {
  /** Design item code (A1/A2/B1/C1…). */
  item: string;
  purpose: ClientConsentPurpose;
  tier: ClientConsentTier;
  title: string;
  /** The exact provider read-aloud copy from design §4. */
  readAloud: string;
  /** Legal basis / helper note shown under the item. */
  note: string;
  /** Whether the item is affirmed by default (Tier B acknowledgement is default ON). */
  defaultGranted: boolean;
  /** True when the item captures a structured scope (named person, photo sub-uses…). */
  hasScope: boolean;
}

export const CLIENT_CONSENT_DEFS: Record<ClientConsentPurpose, ClientConsentDef> = {
  // ---- Tier A — required to proceed (must be explicitly affirmed) ----
  SERVICE_AGREEMENT: {
    item: 'A1',
    purpose: 'SERVICE_AGREEMENT',
    tier: 'REQUIRED',
    title: 'Service agreement + care & services plan',
    readAloud:
      "This sets out the services we'll provide, how often, and what you'll pay. We build it with you and review it with you at least once a year. Ticking this means you agree to receive these services and for us to prepare the formal service agreement with you. The formal agreement is completed separately by signature or another valid recorded agreement method.",
    note: 'HARD GATE — the formal Support at Home service agreement must be entered before service commencement (subject to the narrow alternative-entry timing rule). The legislation does not prescribe a signature method; retain truthful assent evidence, including a witnessed discussion where Department guidance permits it for a participant who cannot physically sign.',
    defaultGranted: false,
    hasScope: false,
  },
  COLLECT_INFO: {
    item: 'A2',
    purpose: 'COLLECT_INFO',
    tier: 'REQUIRED',
    title: 'Collect your personal & health information',
    readAloud:
      "To care for you safely we need to collect information about your health and situation. We only collect what we need, and we'll give you a privacy notice explaining how we store and protect it.",
    note: 'REQUIRED — Privacy Act APP 3.3 (collecting sensitive/health information needs express consent).',
    defaultGranted: false,
    hasScope: false,
  },
  // ---- Tier B — acknowledge (already authorised by law; default ON) ----
  USE_SHARE_CARE: {
    item: 'B1',
    purpose: 'USE_SHARE_CARE',
    tier: 'ACKNOWLEDGE',
    title: 'Use your information to deliver your care',
    readAloud:
      "The law lets us use your information, and share it with the people involved in your care — your GP, allied health, hospital, and any provider delivering services on our behalf — so we can look after you properly. You can tell us anyone you'd prefer we don't share with.",
    note: 'Authorised by Aged Care Act 2024 s 538(4)(a) (s 537 Note 1). Absorbs the former sub-provider data-sharing consent — an acknowledgement, not a gate.',
    defaultGranted: true,
    hasScope: true,
  },
  // ---- Tier C — optional / conditional (explicit opt-in; service NOT contingent) ----
  CONTACT_NAMED_PERSON: {
    item: 'C1',
    purpose: 'CONTACT_NAMED_PERSON',
    tier: 'OPTIONAL',
    title: 'Talk to specific family or friends',
    readAloud:
      'Who would you like us to be able to talk to about your care, and what can we share with them?',
    note: 'Optional — Privacy Act APP 6 (only needed for people who are not your decision-maker). Capture named people + scope.',
    defaultGranted: false,
    hasScope: true,
  },
  PHOTOS: {
    item: 'C2',
    purpose: 'PHOTOS',
    tier: 'OPTIONAL',
    title: 'Photography / media',
    readAloud:
      'May we take photos? You can say yes to some uses (care records) and no to others (promotion), and change your mind anytime.',
    note: 'Optional, revocable, with sub-choices.',
    defaultGranted: false,
    hasScope: true,
  },
  MHR_PREFERENCE: {
    item: 'C3',
    purpose: 'MHR_PREFERENCE',
    tier: 'OPTIONAL',
    title: 'My Health Record preferences',
    readAloud: "If you use My Health Record, tell us anything you'd prefer we don't upload.",
    note: 'Record a preference, not a consent — standing consent already applies. Only if we use MHR.',
    defaultGranted: false,
    hasScope: true,
  },
  ACD_RECORD: {
    item: 'C4',
    purpose: 'ACD_RECORD',
    tier: 'OPTIONAL',
    title: 'Advance care directive / decision-maker details',
    readAloud:
      "Do you have an advance care directive or an appointed decision-maker? We'll note it and keep a copy.",
    note: 'Record the fact, not a consent.',
    defaultGranted: false,
    hasScope: true,
  },
};

/** Ordered purposes for rendering (A1, A2, B1, C1…C4). */
export const CLIENT_CONSENT_PURPOSES: readonly ClientConsentPurpose[] = [
  'SERVICE_AGREEMENT',
  'COLLECT_INFO',
  'USE_SHARE_CARE',
  'CONTACT_NAMED_PERSON',
  'PHOTOS',
  'MHR_PREFERENCE',
  'ACD_RECORD',
];

export const CLIENT_CONSENT_TIER_LABELS: Record<ClientConsentTier, string> = {
  REQUIRED: 'Required to proceed',
  ACKNOWLEDGE: 'Acknowledge (already authorised by law)',
  OPTIONAL: 'Optional — service is not affected by your choice',
};

/** Purposes that must be explicitly affirmed before onboarding can proceed (Tier A). */
export const REQUIRED_CLIENT_CONSENT_PURPOSES: readonly ClientConsentPurpose[] =
  CLIENT_CONSENT_PURPOSES.filter((p) => CLIENT_CONSENT_DEFS[p].tier === 'REQUIRED');

export function clientConsentDefsForTier(tier: ClientConsentTier): ClientConsentDef[] {
  return CLIENT_CONSENT_PURPOSES.map((p) => CLIENT_CONSENT_DEFS[p]).filter((d) => d.tier === tier);
}

export function isClientConsentPurpose(value: unknown): value is ClientConsentPurpose {
  return (CLIENT_CONSENT_PURPOSES as readonly string[]).includes(String(value ?? ''));
}

/**
 * Reader-compat SSOT: the consent-type tokens that satisfy the SAH "care consent"
 * readiness check. The redesign records the care acknowledgement as
 * `USE_SHARE_CARE`; the legacy onboarding recorded it as `CARE`. Both count so a
 * client onboarded under EITHER shape reads as care-consent satisfied (GB is live).
 */
export const CARE_CONSENT_TYPES: readonly string[] = ['CARE', 'USE_SHARE_CARE'];

/**
 * Reader-compat SSOT (dual-audit fix 3): the consent-type tokens that satisfy the
 * SAH "sub-provider data sharing" check. The redesign ABSORBS the former standalone
 * `SAH_DATA_SHARING_SUBPROVIDERS` consent into the Tier B `USE_SHARE_CARE`
 * acknowledgement (design §2) — the disclosure is care-purpose and already
 * authorised (Aged Care Act 2024 ss 537–538), with transparency discharged by the
 * service agreement naming the associated provider. A client onboarded under EITHER
 * shape reads as sub-provider-sharing satisfied (GB is live). Mirrors the DB helper
 * `sah_client_consent_granted` which expands SAH_DATA_SHARING_SUBPROVIDERS the same way.
 */
export const SUB_PROVIDER_CONSENT_TYPES: readonly string[] = [
  'SAH_DATA_SHARING_SUBPROVIDERS',
  'USE_SHARE_CARE',
];

/** Does a consents row satisfy the sub-provider data-sharing check (old token OR USE_SHARE_CARE)? */
export function isGrantedSubProviderConsent(
  row: { consentType?: string | null; status?: string | null } | null | undefined,
): boolean {
  if (!row) return false;
  const type = String(row.consentType ?? '');
  const status = String(row.status ?? '')
    .trim()
    .toUpperCase();
  return (
    SUB_PROVIDER_CONSENT_TYPES.includes(type) &&
    (status === 'GIVEN' || status === 'GRANTED' || status === 'ACTIVE' || status === 'ACCEPTED')
  );
}

/** A named person the participant opts to let us talk to (design §4 C1). */
export interface ConsentNamedPerson {
  name: string;
  relationship?: string | null;
  /** What may be shared with them. */
  shareScope?: string | null;
}

/**
 * Onboarding consent affirmations captured by the redesigned consent step and
 * sent to `registerClientAction`. The server maps each affirmed item to ONE
 * `record_client_consent_controlled` call (one row per purpose). `consentedBy`
 * is derived from capacity (SUBSTITUTE_REQUIRED → DECISION_MAKER, else CLIENT).
 */
export interface OnboardingConsentAffirmations {
  /** WHO affirmed (derived from decision capacity). */
  consentedBy: ConsentedBy;
  /** The consent-pack text version presented at capture (→ document_version). */
  documentVersion?: string | null;
  /** Capture method — default 'verbal_attested' (provider read-aloud bedside). */
  method?: 'verbal_attested' | 'wet_signature' | 'typed_name' | 'digital_signature';

  // Tier A — required to proceed
  serviceAgreement: boolean; // A1
  collectInfo: boolean; // A2

  // Tier B — acknowledge (default ON)
  useShareCare: boolean; // B1
  /** People/orgs the participant asks us NOT to share with (B1 scope). */
  doNotShareWith?: string | null;

  // Tier C — optional opt-in
  namedPersons?: ConsentNamedPerson[]; // C1
  photography?: { granted: boolean; uses?: string[] } | null; // C2
  mhrPreference?: { recorded: boolean; doNotUpload?: string | null } | null; // C3
  acdRecord?: { hasDirective: boolean; note?: string | null } | null; // C4
}

/** Does a consents row satisfy the "care consent granted" readiness check? */
export function isGrantedCareConsent(
  row: { consentType?: string | null; status?: string | null } | null | undefined,
): boolean {
  if (!row) return false;
  const type = String(row.consentType ?? '');
  const status = String(row.status ?? '')
    .trim()
    .toUpperCase();
  return (
    CARE_CONSENT_TYPES.includes(type) &&
    (status === 'GIVEN' || status === 'GRANTED' || status === 'ACTIVE' || status === 'ACCEPTED')
  );
}

/** Methods accepted by `create_consent_controlled` / `record_client_consent_controlled`. */
export type ConsentControlledMethod =
  | 'typed_name'
  | 'wet_signature'
  | 'digital_signature'
  | 'verbal_attested';

/** Intake consent-availability statuses from the legacy (non-redesign) wizard. */
export type IntakeConsentCaptureStatus =
  | 'GRANTED'
  | 'PENDING_SIGNATURE'
  | 'HARD_COPY_TO_UPLOAD'
  | 'VERBAL_TO_FORMALISE'
  | 'DECLINED'
  | 'NOT_AVAILABLE'
  | 'NOT_APPLICABLE'
  | 'NOT_REQUIRED_YET';

/**
 * Maps a legacy intake capture status to the consent `method` written by
 * `create_consent_controlled`. Only `GRANTED` rows are persisted at registration;
 * never stamp `wet_signature` unless a wet signature was actually captured.
 */
export function intakeConsentMethodFromCaptureStatus(
  status: IntakeConsentCaptureStatus,
): ConsentControlledMethod | null {
  if (status === 'GRANTED') {
    // Legacy intake "granted" is provider attestation at registration — not a wet signature.
    return 'verbal_attested';
  }
  return null;
}
